import _ from 'underscore';
import TemplateHtml from '../html/dialog.html';
import BaseView from './baseView';
import { createButtons } from './genericButton';
import InputManager from './manager/inputManager';
import Router from './router';

/**
 * @typedef {import('./baseView').BaseViewOptions} BaseViewOptions
 * @typedef {import('./genericButton').GenericButtonOptions} GenericButtonOptions
 *
 * @typedef {Object} DialogPartial
 * @property {number} [scrollIncrement]
 * @property {string} [template] - String that can be templatized (using string facilitates loading template from content data)
 * @property {string} [title] - Title to display for the Dialog
 * @property {string} [message] - The message text for the Dialog
 * @property {GenericButtonOptions[]} [btns]
 *
 * @typedef {BaseViewOptions & DialogPartial} DialogOptions
 */

/** @enum {typeof EVENTS[keyof typeof EVENTS]} */
export const EVENTS = Object.freeze({
	closeClicked: 'closed',
});

/**
 * @class
 * @extends {BaseView<DialogOptions>}
 */
export default class Dialog extends BaseView {
	get CN() {
		return Dialog.name;
	}

	id() {
		return 'dialog';
	}

	className() {
		return `default`;
	}

	get template() {
		return _.template(TemplateHtml);
	}

	events() {
		return {
			'click .btn': this.onCloseClicked,
		};
	}

	/**
	 * @param {DialogOptions} options
	 */
	constructor(options) {
		super(options);
		this.logger.log('[constructor]', { ...options });

		/** @type {GenericButtonOptions[]} */
		this.btns = this.btns ?? [];
		/** @type {number} */
		this.scrollIncrement = this.scrollIncrement ?? 250;
		/** @type {number} */
		this.currentScroll = this.scrollIncrement ?? 0;

		// Runtime Properties
		/** @type {boolean} */
		this.isScrolling = false;

		const id = _.result(this, 'id');
		InputManager.add(id, {
			selector: `#${id} .btn, #${id} button`,
			restrict: 'self-only',
		});
	}

	/**
	 *
	 * @param {DialogOptions} options
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]<${Dialog.name}>`, { options });

		const { template, ...viewOptions } = options;
		if (template) {
			// this.template is a getter and must be redefined if provided in the options
			// * One does not simply apply() getters *
			Object.defineProperty(this, 'template', {
				get() {
					return _.template(template);
				},
				enumerable: true,
				configurable: true,
			});
		}

		Object.assign(this, viewOptions);
		this.options = options;

		this.onCloseClicked = this.onCloseClicked.bind(this);
		this.onScroll = this.onScroll.bind(this);

		this.render();

		$(window).on('sn:willmove', this.onScroll);
	}

	render() {
		this.logger.log(`[${this.render.name}]`, this);

		super.render();

		/** @type {GenericButtonOptions[]} */
		const btns = this.btns ?? [];
		if (!btns?.length) {
			btns.push(this.getACloseButton());
		}
		this.$('.btn-container').append(createButtons(btns));

		if (!this.$('.btn').length) {
			this.$el.prepend(createButtons(this.getACloseButton()));
		}

		const numButtons = this.$('.btn').length;
		const numCloseButtons = this.$('.btn-close').length;
		if (numButtons === 0 || numCloseButtons === 1) {
			Router.overrideBackNavigation(this.onCloseClicked);
		}

		this.attachDecorators();

		return Promise.resolve(this);
	}

	getACloseButton() {
		return { id: 'close', text: ['Close'], className: 'btn btn-close' }; // Always have a close button
	}

	onScroll() {
		if (!document.body.contains(this.el)) {
			return;
		}

		let event;
		if (arguments.length) {
			event = arguments[0];
		}

		const { direction } = event.detail;
		this.logger.debug('[onScroll]', { direction });

		if (direction === 'up') {
			this.scrollUp();
		} else if (direction === 'down') {
			this.scrollDown();
		}
	}

	scrollUp() {
		if (this.isScrolling) {
			return;
		}

		this.logger.log(`[${this.scrollUp.name}]`);

		const content = this.$el.find('.popup-content').eq(0) || this.$el;
		this.currentScroll = Math.max(0, this.currentScroll - this.scrollIncrement);

		this.animateScroll(content);
	}

	scrollDown() {
		if (this.isScrolling) {
			return;
		}

		this.logger.log(`[${this.scrollDown.name}]`);

		const content = this.$el.find('.popup-content').eq(0) || this.$el;
		const bottom = content.get(0)?.scrollHeight || this.currentScroll + this.scrollIncrement; // ! Infinity?

		this.currentScroll = Math.min(bottom, this.currentScroll + this.scrollIncrement);

		this.animateScroll(content);
	}

	/**
	 * @param {JQuery<HTMLElement>} $element
	 */
	animateScroll($element) {
		$element.animate(
			{ scrollTop: this.currentScroll },
			{
				start: () => (this.isScrolling = true),
				complete: () => (this.isScrolling = false),
			},
		);
	}

	onCloseClicked() {
		this.logger.log(`[${this.onCloseClicked.name}]`);

		this.trigger(EVENTS.closeClicked);
	}

	destroy() {
		this.logger.log(`[${this.destroy.name}]`);

		Router.removeBackOverride(this.onCloseClicked);
		$(window).off('sn:willmove', this.onScroll);
		super.destroy();
	}
}
